interface Props {
  title?: string;
  message?: string;
  quickReplies?: string[];
  onQuickReply: (text: string) => void;
}

/**
 * Welcome bubble shown above the empty message list (Tawk / Intercom style).
 * Renders a bot avatar + intro card + quick-reply chips that, when clicked,
 * fire a normal chat message via the host's send callback.
 *
 * If no title/message/quickReplies are passed, the component renders nothing
 * — hosts opt in via web-component attributes.
 */
export function ChatIntro({ title, message, quickReplies, onQuickReply }: Props) {
  if (!title && !message && (!quickReplies || quickReplies.length === 0)) return null;

  return (
    <div className="px-4 pt-4 pb-2">
      {title && (
        <div className="text-xs font-semibold text-gray-500 mb-2 pl-10">{title}</div>
      )}
      {message && (
        <div className="flex items-start gap-2 mb-3">
          <div className="vip-btn-primary shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center text-sm font-semibold">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
            </svg>
          </div>
          <div className="vip-btn-primary text-white text-sm rounded-2xl rounded-tl-md px-4 py-3 max-w-[85%] whitespace-pre-wrap leading-relaxed shadow-sm">
            {message}
          </div>
        </div>
      )}
      {quickReplies && quickReplies.length > 0 && (
        <div className="flex flex-col items-end gap-2 pl-10">
          {quickReplies.map(reply => (
            <button
              key={reply}
              type="button"
              onClick={() => onQuickReply(reply)}
              className="text-sm font-medium px-4 py-2 rounded-full bg-white border border-blue-200 text-blue-700 hover:bg-blue-50 transition shadow-sm"
              style={{ borderColor: 'var(--vip-primary)', color: 'var(--vip-primary)' }}
            >
              {reply}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
